import React, { useState } from "react"
import { format } from "date-fns"
import type { Suggestion } from "../../../types/search"

interface SearchSuggestionsProps {
  suggestions: Suggestion[]
  highlightedIndex: number
  onHighlight: (index: number) => void
  onSelect: (item: Suggestion) => void
}

const isPostLike = (
  item: Suggestion,
): item is Extract<Suggestion, { title: string }> =>
  item.type === "song" ||
  item.type === "album" ||
  item.type === "post" ||
  item.type === "event"

const suggestionLabel = (item: Suggestion): string =>
  isPostLike(item) ? item.title : item.name

const suggestionDetail = (item: Suggestion): string | null => {
  if (item.type === "event") {
    const dateLabel = item.start_date
      ? format(new Date(item.start_date), "MMM d, yyyy")
      : null
    return [dateLabel, item.venue_name].filter(Boolean).join(" - ") || null
  }
  if (isPostLike(item)) {
    return (
      [item.post_type_nice, item.band_name].filter(Boolean).join(" - ") || null
    )
  }
  return item.band_type_nice || null
}

const SearchSuggestions = ({
  suggestions,
  highlightedIndex,
  onHighlight,
  onSelect,
}: SearchSuggestionsProps) => {
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({})

  return (
  <ul
    id="search-suggestions-listbox"
    className="absolute top-full left-0 right-0 bg-white border border-t-0 border-gray-500 rounded-b shadow-lg z-30 max-h-96 overflow-y-auto"
    role="listbox"
  >
    {suggestions.map((item, idx) => {
      const label = suggestionLabel(item)
      const detail = suggestionDetail(item)
      const key = `${item.type}-${item.id}`
      const showImage = item.image_url && !failedImages[key]
      return (
        <li
          key={key}
          role="option"
          aria-selected={highlightedIndex === idx}
          className={`px-2 py-2 cursor-pointer ${
            highlightedIndex === idx ? "bg-gray-100" : ""
          }`}
          onMouseEnter={() => onHighlight(idx)}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onSelect(item)}
        >
          <div className="flex items-center">
            {showImage ? (
              <img
                src={item.image_url as string}
                alt={`${label} cover art`}
                loading="lazy"
                onError={() =>
                  setFailedImages((prev) => ({ ...prev, [key]: true }))
                }
                className="w-8 h-8 object-cover flex-shrink-0 mr-2 bg-gray-200"
              />
            ) : (
              <div className="w-8 h-8 flex-shrink-0 mr-2 bg-black flex items-center justify-center text-white font-bold uppercase">
                {label.charAt(0)}
              </div>
            )}
            <div className="min-w-0">
              <div className="font-bold leading-tight text-black-800 truncate">
                {label}
              </div>
              {detail && (
                <div className="font-medium leading-tight text-gray-600 text-xxs uppercase truncate">
                  {detail}
                </div>
              )}
            </div>
          </div>
        </li>
      )
    })}
  </ul>
  )
}

export default SearchSuggestions
